import {useContext, useRef, useState} from "react";
import {Modal} from "react-bootstrap";
import LiveApiClient from "../../../api/LiveApiClient";
import {processImagePath} from "../../../util/Functions";
import LoadMask from "../../../components/LoadMask";

import ApiKeyContext from "../../../contexts/ApiKeyContext";
import {useProductDetailContext} from "../../../hooks/useProductDetailContext";

import "./CatalogSearch.scss";

export default function CatalogSearch() {
	const API_KEY_CONTEXT = useContext(ApiKeyContext);
	const {setModalProps} = useProductDetailContext();
	
	const modalBodyRef = useRef(null);
	const abort_controller = useRef(new AbortController());
	
	const [showModal, setShowModal] = useState(false);
	const searchDebounce = useRef(undefined);
	const [isLoading, setIsLoading] = useState(undefined);
	const [results, setResults] = useState(undefined);
	const [searchValue, setSearchValue] = useState(undefined);
	const [loadedSearchValue, setLoadedSearchValue] = useState(undefined);
	
	const showSearchModal = () => {
		setShowModal(true);
		setTimeout(() => document.querySelector('.search-modal input[type="search"]')?.focus(), 10);
	};
	const hideSearchModal = () => {
		setSearchValue(undefined);
		setResults(undefined);
		setShowModal(false);
	};
	
	const doSearch = searchValue => {
		setSearchValue(searchValue);
		const delay = 1000;
		clearTimeout(searchDebounce.current);
		
		searchDebounce.current = setTimeout(() => {
			// Cancel outstanding
			if (loadedSearchValue === searchValue) {
				return;
			}
			const options = {
				headers: {
					'api-key': API_KEY_CONTEXT.apiKey,
					'cache-control': !localStorage.getItem('nocache') && 'private',
					account: API_KEY_CONTEXT.account,
				},
				params: {
					q: searchValue,
					limit: 36,
					count: false,
					paths: JSON.stringify({
						common_sku: true,
						name: true,
						description: true,
						media: ['type', 'source'],
					})
				},
				signal: abort_controller.current.signal
			};
			setIsLoading(true);
			modalBodyRef.current.scrollTo({top: 0, behavior: 'smooth'});
			LiveApiClient.get(`/Item`, options).then(response => {
				setResults(response.data);
				setIsLoading(false);
				setLoadedSearchValue(searchValue);
			});
		}, delay);
	};
	
	const showItem = sku => setModalProps({
		item: sku,
		fullscreen: true,
	});
	
	const generateItemRow = item => {
		const images = item.media.filter(media => media.type === 'image');
		return (
			<div className="item" key={item.common_sku} onClick={() => showItem(item.common_sku)}>
				<div className={'image-wrapper' + (images[1] ? ' has-second' : '')}>
					{images[0] && (<img alt="Product Thumb" src={processImagePath(images[0].source, 'th')}/>)}
					{images[1] && (<img alt="Product Thumb" src={processImagePath(images[1].source, 'th')}/>)}
				</div>
				<div className="details">
					<div className="name">{item.name || item.description || ''}</div>
					<div className="sku">{item.common_sku}</div>
				</div>
			</div>)
	}
	
	const searchModal =
		(
			<Modal
				show={showModal}
				className={'_boss search-modal' + (searchValue ? ' search-active' : '')}
				backdropClassName='search-modal-backdrop'
				backdrop={true}
				aria-labelledby="contained-modal-title-vcenter"
				onEscapeKeyDown={hideSearchModal}
				onHide={hideSearchModal}
			>
				<Modal.Body ref={modalBodyRef}>
					<div className="catalog-search-wrapper">
						<div className="search-header">
							<div className="search-input-wrapper iconable">
								<input type="search"
								       placeholder="Search for Products..."
								       onKeyUp={e => doSearch(e.target.value)}
								       onFocus={e => e.target.parentNode.classList.add('has-focus')}
								       onBlur={e => e.target.parentNode.classList.remove('has-focus')}
								/>
							</div>
							<button role="button" className="close" onClick={() => setShowModal(false)}></button>
						</div>
						<div className="results-wrapper">
							<LoadMask show={isLoading}/>
							{ results?.length > 0 && (
								<div className="results">
									{results.map(generateItemRow)}
								</div>
							)}
							{ results?.length === 0 && searchValue && (
								<h4 className="no-results">No Products Found</h4>
							)}
						</div>
					</div>
				</Modal.Body>
                <Modal.Footer />
			</Modal>
		);
	
	return (
		<>
			<div className="global-search">
				<div title="Search the Catalog" role="button" onClick={showSearchModal}>
					<i className="fal fa-search"></i><span>Search</span>
				</div>
			</div>
			{searchModal}
		</>
	
	);
}
